import React, {useState, useEffect} from "react";
import {
    Activity,
    Book,
    Users,
    Settings,
    LogOut,
    Sun,
    Moon,
    X,
    CalendarArrowDown,
    ArrowUpFromDotIcon,
    FileText,
    Link2,
    ChevronLeft,
    ChevronRight,
    Menu,
    RefreshCw,
    Megaphone,
    Heart,
    RotateCcw,
    Fingerprint,
    Store,
    Languages,
    WifiOff,
    ShoppingBag,
    ShoppingCart,
} from "lucide-react";
import {NavLink, Outlet, useNavigate, useLocation} from "react-router-dom";
import useAuthStore from "../../stores/authStore";
import secureStorage from "../../utils/secureStorage";
import {AnimatePresence, motion} from "framer-motion";
import {useTranslation} from "react-i18next";
import {useDarkMode} from "../../contexts/DarkModeContext";
import {SUPPORTED_LANGUAGES} from "../../i18n";
import useOnlineStatus from "../../hooks/useOnlineStatus";
import useAttendanceSettings from "../Attendance/useAttendanceSettings";
import {isRouteAvailableForAccount} from "../../utils/permissionHelper";

// "New" pill next to a menu label; a dot on the icon when the rail is collapsed.
const NewBadge = ({label}) => (
    <span className="px-1.5 py-0.5 rounded-md bg-primary text-[10px] font-semibold uppercase tracking-wide text-white leading-none">
        {label}
    </span>
);
const NewDot = () => (
    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary ring-2 ring-gray-900"/>
);

// ─── constants ───────────────────────────────────────────────────────────────
const COLLAPSED_W = 64;   // px – icon-only rail
const EXPANDED_W = 256;  // px – full sidebar

const Sidebar = () => {
    const {t, i18n} = useTranslation();
    // Persist sidebar-open state across page loads / navigation
    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            return localStorage.getItem("sidebarCollapsed") === "true";
        } catch {
            return false;
        }
    });
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [email, setEmail] = useState("");
    const [account, setAccount] = useState(null);
    const {isDarkMode, toggleDarkMode} = useDarkMode();
    const isOnline = useOnlineStatus();

    const logout = useAuthStore(state => state.logout);
    const navigate = useNavigate();
    const location = useLocation();

    // ── responsive ────────────────────────────────────────────────────────────
    useEffect(() => {
        const onResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) setIsMobileOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    // ── persist last visited route + route history ────────────────────────────
    useEffect(() => {
        const authRoutes = ["/login", "/select-account", "/changePassword", "/forgoutPassword", "/logout"];
        if (authRoutes.includes(location.pathname)) return;

        // Normalize sub-routes to their parent so history always restores a
        // meaningful landing page (e.g. /NewProductForm → /catalogue).
        const normalizeRoute = (p) => {
            const catalogueSubs = ['/NewProductForm', '/NewCategoryForm', '/NewBrandForm', '/NewRemiseForm'];
            if (catalogueSubs.includes(p)) return '/catalogue';
            if (p.startsWith('/catalogue/')) return '/catalogue';
            if (p.startsWith('/clients/')) return '/clients';
            if (p.startsWith('/integrations/')) return '/integrations';
            if (p.startsWith('/settings/')) return '/settings';
            if (p.startsWith('/storefront/')) return '/storefront';
            if (p.startsWith('/stock/') || p === '/inventaire' || p.startsWith('/inventaire/')) return '/stock';
            if (p.startsWith('/technical-files/')) return '/technical-files';
            return p;
        };

        const path = normalizeRoute(location.pathname);

        // Save as last single route (used for redirect after login)
        localStorage.setItem("lastRoute", path);

        // Append to history array (max 30 entries, no consecutive duplicates)
        try {
            const stored = localStorage.getItem("routeHistory");
            const history = stored ? JSON.parse(stored) : [];
            if (history[history.length - 1] !== path) {
                history.push(path);
                if (history.length > 30) history.shift();
                localStorage.setItem("routeHistory", JSON.stringify(history));
            }
        } catch { /* ignore parse errors */
        }
    }, [location.pathname]);

    // ── account data ────────────────────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const selectedAccount = await secureStorage.getItem("selectedAccount");
                if (selectedAccount !== "") {
                    setEmail(await secureStorage.getItem("_email"));
                    setAccount(selectedAccount);
                }
            } catch (err) {
                console.error("Error fetching selected account:", err);
            }
        })();
    }, []);

    // ── helpers ───────────────────────────────────────────────────────────────
    const toggleCollapse = () => {
        setIsCollapsed(prev => {
            const next = !prev;
            localStorage.setItem("sidebarCollapsed", next);
            return next;
        });
    };

    const handleLogout = async () => {
        await logout();
        navigate("/login");
    };

    const changeLanguage = (lng) => i18n.changeLanguage(lng);

    // ── menu data ─────────────────────────────────────────────────────────────
    const {enabled: attendanceEnabled} = useAttendanceSettings();
    const menuItems = [
        {icon: <Activity size={20}/>, label: t("nav.activite"), path: "/activite"},
        {icon: <ShoppingCart size={20}/>, label: t("nav.pos"), path: "/caisse", isNew: true},
        {icon: <Book size={20}/>, label: t("nav.catalogue"), path: "/catalogue"},
        {icon: <Users size={20}/>, label: t("nav.clients"), path: "/clients"},
        {icon: <Heart size={20}/>, label: t("nav.loyalty"), path: "/fidelite"},
        {icon: <RotateCcw size={20}/>, label: t("nav.returns"), path: "/retours"},
        ...(attendanceEnabled ? [{icon: <Fingerprint size={20}/>, label: t("nav.attendance"), path: "/pointage"}] : []),
        {icon: <ArrowUpFromDotIcon size={20}/>, label: t("nav.stock"), path: "/stock"},
        // Fiches techniques (recettes) are restaurant-only (busniessType "004")
        ...(isRouteAvailableForAccount("/technical-files", account)
            ? [{icon: <FileText size={20}/>, label: t("nav.technicalFiles"), path: "/technical-files"}]
            : []),
        {icon: <Megaphone size={20}/>, label: t("nav.marketing"), path: "/marketing"},
        {icon: <Store size={20}/>, label: t("nav.storefront"), path: "/storefront"},
        {icon: <Link2 size={20}/>, label: t("nav.integrations"), path: "/integrations"},
        {
            icon: <CalendarArrowDown size={20} className="text-amber-400"/>,
            label: <span className="text-amber-400">{t("nav.calendarBeta")}</span>,
            path: "/calander",
        },
    ];

    const bottomItems = [
        {icon: <ShoppingBag size={20}/>, label: t("nav.marketplace"), path: "/marketplace", isNew: true},
        {icon: <Settings size={20}/>, label: t("nav.settings"), path: "/settings"},
        {icon: <LogOut size={20}/>, label: t("nav.logout"), onClick: handleLogout},
    ];

    // ── current page title (used by mobile top bar) ───────────────────────────
    const getPageTitle = () => {
        const p = location.pathname;
        // Map a sub-route to its parent section so the title stays meaningful.
        const sectionFor = (path) => {
            if (['/NewProductForm', '/NewCategoryForm', '/NewBrandForm', '/NewRemiseForm'].includes(path)) return '/catalogue';
            if (path.startsWith('/catalogue/')) return '/catalogue';
            if (path.startsWith('/clients/')) return '/clients';
            if (path.startsWith('/integrations/')) return '/integrations';
            if (path.startsWith('/settings/')) return '/settings';
            if (path.startsWith('/storefront/')) return '/storefront';
            if (path.startsWith('/stock/') || path === '/inventaire' || path.startsWith('/inventaire/')) return '/stock';
            if (path.startsWith('/technical-files/')) return '/technical-files';
            return path;
        };
        const section = sectionFor(p);
        const match = [...menuItems, ...bottomItems].find((item) => item.path === section);
        // labels may be JSX (e.g. Calendrier) — fall back to plain strings only.
        if (match && typeof match.label === "string") return match.label;
        if (section === "/calander") return t("nav.calendar");
        return t("app.name");
    };

    // ── avatar initials ───────────────────────────────────────────────────────
    const initials = account?.name
        ? account.name.slice(0, 2).toUpperCase()
        : email
            ? email.slice(0, 2).toUpperCase()
            : "";

    const Avatar = () => (
        account?.logo ? (
            <img src={account.logo} alt="Account Logo" className="w-full h-full object-cover rounded-full"/>
        ) : (
            <div
                className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center border border-teal-400">
                <span className="text-xs font-semibold text-teal-400">{initials}</span>
            </div>
        )
    );

    // ── shared nav-link class factory ─────────────────────────────────────────
    const navClass = ({isActive}) =>
        `flex items-center transition-colors duration-150 ${
            isCollapsed ? "justify-center px-0 py-4 w-full" : "px-6 py-3.5 space-x-4"
        } ${isActive
            ? "text-white bg-green-500/10 border-l-4 border-primary"
            : "hover:bg-gray-800/50"
        }`;

    // =========================================================================
    //  DESKTOP SIDEBAR
    // =========================================================================
    const renderDesktopSidebar = () => (
        <motion.aside
            animate={{width: isCollapsed ? COLLAPSED_W : EXPANDED_W}}
            transition={{duration: 0.3, ease: "easeInOut"}}
            className="fixed top-0 left-0 h-screen bg-[#242331] dark:bg-gray-900 text-gray-400 flex flex-col z-50 shadow-lg"
            style={{minWidth: isCollapsed ? COLLAPSED_W : EXPANDED_W}}
        >
            {/* ── Logo / brand ── */}
            <div
                className={`flex items-center border-b border-gray-700/30 transition-all duration-300 overflow-hidden ${isCollapsed ? "justify-center py-4 px-0" : "justify-between px-6 py-5"}`}>
                {isCollapsed ? (
                    <img src="/quickcom-logo-shape.png" alt="Quickcom" className="h-7 w-7"/>
                ) : (
                    <img src="/quickcom-logo-white.png" alt="Quickcom Logo" className="h-8"/>
                )}
            </div>

            {/* ── Offline indicator (only rendered while offline) ── */}
            <AnimatePresence>
                {!isOnline && (
                    <motion.div
                        initial={{opacity: 0, height: 0}}
                        animate={{opacity: 1, height: "auto"}}
                        exit={{opacity: 0, height: 0}}
                        transition={{duration: 0.2}}
                        className="overflow-hidden"
                    >
                        <div
                            className={`flex items-center gap-2 text-xs font-medium text-red-300 bg-red-500/10 border-b border-gray-700/30 ${
                                isCollapsed ? "justify-center py-2" : "px-4 py-2"
                            }`}
                            title={t("sidebar.offline")}
                        >
                            <WifiOff size={16} className="shrink-0"/>
                            {!isCollapsed && <span>{t("sidebar.offline")}</span>}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Dark-mode toggle (hidden when collapsed) ── */}
            <AnimatePresence>
                {!isCollapsed && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.2}}
                        className="px-4 py-3 border-b border-gray-700/30"
                    >
                        <button
                            onClick={toggleDarkMode}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-800/50 transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                {isDarkMode
                                    ? <Moon size={20} className="text-blue-400"/>
                                    : <Sun size={20} className="text-yellow-400"/>}
                                <span
                                    className="text-sm whitespace-nowrap">{isDarkMode ? t("sidebar.darkMode") : t("sidebar.lightMode")}</span>
                            </div>
                            <span className="w-10 h-5 bg-gray-700 rounded-full flex items-center p-1">
                                <span
                                    className={`w-3 h-3 rounded-full bg-white transform transition-transform ${isDarkMode ? "translate-x-5" : "translate-x-0"}`}/>
                            </span>
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Language switcher (hidden when collapsed) ── */}
            <AnimatePresence>
                {!isCollapsed && (
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.2}}
                        className="px-4 py-3 border-b border-gray-700/30"
                    >
                        <div className="flex items-center justify-between px-3">
                            <div className="flex items-center space-x-3">
                                <Languages size={20} className="text-gray-400"/>
                                <span className="text-sm whitespace-nowrap">{t("language.label")}</span>
                            </div>
                            <div className="flex items-center gap-1 bg-gray-800/60 rounded-md p-0.5">
                                {SUPPORTED_LANGUAGES.map((lng) => (
                                    <button
                                        key={lng}
                                        onClick={() => changeLanguage(lng)}
                                        className={`px-2 py-1 rounded text-xs font-medium uppercase transition-colors ${
                                            i18n.language === lng
                                                ? "bg-primary text-white"
                                                : "text-gray-400 hover:text-white"
                                        }`}
                                    >
                                        {lng}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Main nav ── */}
            <nav className="flex-1 py-4 overflow-y-auto overflow-x-hidden">
                {!isCollapsed && (
                    <div className="px-4 mb-3">
                        <p className="text-xs uppercase text-gray-500 font-semibold tracking-wider">{t("sidebar.navigationSection")}</p>
                    </div>
                )}
                {menuItems.map((item, i) => (
                    <NavLink key={i} to={item.path} className={navClass}
                             title={isCollapsed ? (typeof item.label === "string" ? item.label : "") : undefined}>
                        {() => (
                            <>
                                <span className="relative shrink-0">
                                    {item.icon}
                                    {item.isNew && isCollapsed && <NewDot/>}
                                </span>
                                {!isCollapsed && (
                                    <motion.span
                                        initial={{opacity: 0}}
                                        animate={{opacity: 1}}
                                        exit={{opacity: 0}}
                                        className={`text-sm font-medium whitespace-nowrap overflow-hidden ${item.isNew ? "flex-1" : ""}`}
                                    >
                                        {item.label}
                                    </motion.span>
                                )}
                                {!isCollapsed && item.isNew && <NewBadge label={t("nav.new")}/>}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {/* ── Bottom nav ── */}
            <div className="border-t border-gray-700/30 pt-3 pb-4">
                {!isCollapsed && (
                    <div className="px-4 mb-2">
                        <p className="text-xs uppercase text-gray-500 font-semibold tracking-wider">{t("sidebar.systemSection")}</p>
                    </div>
                )}
                {bottomItems.map((item, i) =>
                    item.path ? (
                        <NavLink key={i} to={item.path} className={navClass}
                                 title={isCollapsed ? item.label : undefined}>
                            {() => (
                                <>
                                    <span className="relative shrink-0">
                                        {item.icon}
                                        {item.isNew && isCollapsed && <NewDot/>}
                                    </span>
                                    {!isCollapsed &&
                                        <span className="flex-1 text-sm font-medium whitespace-nowrap">{item.label}</span>}
                                    {!isCollapsed && item.isNew && <NewBadge label={t("nav.new")}/>}
                                </>
                            )}
                        </NavLink>
                    ) : (
                        <button
                            key={i}
                            onClick={item.onClick}
                            title={isCollapsed ? item.label : undefined}
                            className={`w-full flex items-center transition-colors hover:bg-gray-800/50 text-red-400 hover:text-red-300 ${
                                isCollapsed ? "justify-center py-4" : "px-6 py-3.5 space-x-4"
                            }`}
                        >
                            <span className="shrink-0">{item.icon}</span>
                            {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                        </button>
                    )
                )}
            </div>

            {/* ── User profile ── */}
            <div className={`border-t border-gray-700/30 ${isCollapsed ? "p-2" : "p-4"}`}>
                <div
                    className={`flex items-center hover:bg-gray-800/30 rounded-lg transition-colors cursor-pointer ${isCollapsed ? "justify-center p-2" : "space-x-3 p-2"}`}
                    onClick={() => navigate("/select-account")}
                    title={t("sidebar.changeAccount")}
                >
                    <div
                        className="w-8 h-8 rounded-full shrink-0 bg-gray-800 flex items-center justify-center border border-gray-600 overflow-hidden">
                        <Avatar/>
                    </div>
                    {!isCollapsed && (
                        <div className="flex-1 overflow-hidden">
                            <div className="flex items-center gap-1.5">
                                <p className="text-sm text-white font-medium truncate">{account?.name || (email && email.split("@")[0].toUpperCase())}</p>
                                <RefreshCw size={11} className="text-teal-400 shrink-0"/>
                            </div>
                            <p className="text-xs text-gray-400 truncate">{email}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Collapse toggle button ── */}
            <button
                onClick={toggleCollapse}
                aria-label={isCollapsed ? t("sidebar.expandSidebar") : t("sidebar.collapseSidebar")}
                className={`absolute bottom-[7.5rem] ${isCollapsed ? "right-0 translate-x-1/2" : "-right-3"} w-6 h-6 rounded-full bg-[#4DB6AC] text-white flex items-center justify-center shadow-md hover:bg-teal-400 transition-colors z-10`}
            >
                {isCollapsed ? <ChevronRight size={14}/> : <ChevronLeft size={14}/>}
            </button>
        </motion.aside>
    );

    // =========================================================================
    //  MOBILE OVERLAY DRAWER
    // =========================================================================
    const renderMobileDrawer = () => (
        <AnimatePresence>
            {isMobileOpen && (
                <>
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.2}}
                        className="fixed inset-0 bg-black/50 z-[55]"
                        onClick={() => setIsMobileOpen(false)}
                    />
                    <motion.div
                        initial={{x: "-100%"}}
                        animate={{x: 0}}
                        exit={{x: "-100%"}}
                        transition={{duration: 0.3, ease: "easeInOut"}}
                        className="fixed left-0 top-0 h-screen w-72 bg-[#242331] dark:bg-gray-900 text-gray-400 flex flex-col z-[60] shadow-2xl overflow-y-auto"
                    >
                        {/* header */}
                        <div className="p-6 flex justify-between items-center border-b border-gray-700/30">
                            <img src="/quickcom-logo-white.png" alt="Quickcom Logo" className="h-8"/>
                            <button
                                onClick={() => setIsMobileOpen(false)}
                                className="p-2 hover:bg-gray-800/50 rounded-lg transition-colors"
                                aria-label={t("sidebar.closeMenu")}
                            >
                                <X size={22} className="text-gray-400 hover:text-white"/>
                            </button>
                        </div>

                        {/* offline indicator */}
                        {!isOnline && (
                            <div
                                className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-red-300 bg-red-500/10 border-b border-gray-700/30"
                            >
                                <WifiOff size={16} className="shrink-0"/>
                                <span>{t("sidebar.offline")}</span>
                            </div>
                        )}

                        {/* dark mode */}
                        <div className="px-4 py-3 border-b border-gray-700/30">
                            <button
                                onClick={toggleDarkMode}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-gray-800/50 transition-colors"
                            >
                                <div className="flex items-center space-x-3">
                                    {isDarkMode ? <Moon size={20} className="text-blue-400"/> :
                                        <Sun size={20} className="text-yellow-400"/>}
                                    <span
                                        className="text-sm">{isDarkMode ? t("sidebar.darkMode") : t("sidebar.lightMode")}</span>
                                </div>
                                <span className="w-10 h-5 bg-gray-700 rounded-full flex items-center p-1">
                                    <span
                                        className={`w-3 h-3 rounded-full bg-white transform transition-transform ${isDarkMode ? "translate-x-5" : "translate-x-0"}`}/>
                                </span>
                            </button>
                        </div>

                        {/* language */}
                        <div className="px-4 py-3 border-b border-gray-700/30">
                            <div className="flex items-center justify-between px-3">
                                <div className="flex items-center space-x-3">
                                    <Languages size={20} className="text-gray-400"/>
                                    <span className="text-sm">{t("language.label")}</span>
                                </div>
                                <div className="flex items-center gap-1 bg-gray-800/60 rounded-md p-0.5">
                                    {SUPPORTED_LANGUAGES.map((lng) => (
                                        <button
                                            key={lng}
                                            onClick={() => changeLanguage(lng)}
                                            className={`px-2 py-1 rounded text-xs font-medium uppercase transition-colors ${
                                                i18n.language === lng
                                                    ? "bg-primary text-white"
                                                    : "text-gray-400 hover:text-white"
                                            }`}
                                        >
                                            {lng}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* nav */}
                        <nav className="flex-1 py-4">
                            <div className="px-4 mb-3">
                                <p className="text-xs uppercase text-gray-500 font-semibold tracking-wider">{t("sidebar.navigationSection")}</p>
                            </div>
                            {menuItems.map((item, i) => (
                                <NavLink
                                    key={i}
                                    to={item.path}
                                    onClick={() => setIsMobileOpen(false)}
                                    className={({isActive}) =>
                                        `flex items-center px-6 py-3.5 space-x-4 transition-colors ${
                                            isActive ? "text-white bg-green-500/10 border-l-4 border-primary" : "hover:bg-gray-800/50"
                                        }`
                                    }
                                >
                                    {() => (
                                        <>
                                            <span>{item.icon}</span>
                                            <span className="flex-1 text-sm font-medium">{item.label}</span>
                                            {item.isNew && <NewBadge label={t("nav.new")}/>}
                                        </>
                                    )}
                                </NavLink>
                            ))}
                        </nav>

                        {/* bottom */}
                        <div className="border-t border-gray-700/30 pt-3 pb-4">
                            <div className="px-4 mb-2">
                                <p className="text-xs uppercase text-gray-500 font-semibold tracking-wider">{t("sidebar.systemSection")}</p>
                            </div>
                            {bottomItems.map((item, i) =>
                                item.path ? (
                                    <NavLink
                                        key={i}
                                        to={item.path}
                                        onClick={() => setIsMobileOpen(false)}
                                        className={({isActive}) =>
                                            `flex items-center px-6 py-3.5 space-x-4 transition-colors ${
                                                isActive ? "text-white bg-green-500/10 border-l-4 border-primary" : "hover:bg-gray-800/50"
                                            }`
                                        }
                                    >
                                        {() => (
                                            <>
                                                <span>{item.icon}</span>
                                                <span className="flex-1 text-sm font-medium">{item.label}</span>
                                                {item.isNew && <NewBadge label={t("nav.new")}/>}
                                            </>
                                        )}
                                    </NavLink>
                                ) : (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            item.onClick();
                                            setIsMobileOpen(false);
                                        }}
                                        className="w-full flex items-center px-6 py-3.5 space-x-4 hover:bg-gray-800/50 transition-colors text-red-400 hover:text-red-300"
                                    >
                                        <span>{item.icon}</span>
                                        <span className="text-sm font-medium">{item.label}</span>
                                    </button>
                                )
                            )}
                        </div>

                        {/* user */}
                        <div className="p-4 border-t border-gray-700/30">
                            <div
                                className="flex items-center space-x-3 p-2 hover:bg-gray-800/30 rounded-lg transition-colors cursor-pointer"
                                onClick={() => {
                                    navigate("/select-account");
                                    setIsMobileOpen(false);
                                }}
                                title={t("sidebar.changeAccount")}
                            >
                                <div
                                    className="w-8 h-8 rounded-full shrink-0 bg-gray-800 flex items-center justify-center border border-gray-600 overflow-hidden">
                                    <Avatar/>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <p className="text-sm text-white font-medium truncate">{account?.name || (email && email.split("@")[0].toUpperCase())}</p>
                                    <p className="text-xs text-gray-400 truncate">{email}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );

    // =========================================================================
    //  RENDER
    // =========================================================================
    const sidebarWidth = isMobile ? 0 : isCollapsed ? COLLAPSED_W : EXPANDED_W;

    return (
        <div className="flex dark:bg-gray-900 transition-colors duration-200">
            {/* Sidebars */}
            {!isMobile && renderDesktopSidebar()}
            {isMobile && renderMobileDrawer()}

            {/* Content area – shifts to match sidebar width */}
            <motion.div
                animate={{marginLeft: sidebarWidth, marginRight: isMobile ? 0 : 16}}
                transition={{duration: 0.3, ease: "easeInOut"}}
                className="flex-1 flex flex-col min-h-screen dark:bg-gray-900 dark:text-white transition-colors duration-200"
                style={{minWidth: 0}}
            >
                {/* ── Mobile top bar — fixed 4rem high (pages size themselves with 100dvh - 4rem) ── */}
                {isMobile && (
                    <div
                        className="relative h-16 flex-shrink-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700/30 px-4 flex items-center justify-between shadow-sm">
                        <button
                            onClick={() => setIsMobileOpen(true)}
                            className="relative z-10 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 active:scale-95"
                            aria-label={t("sidebar.openMenu")}
                        >
                            <Menu size={22} className="text-gray-700 dark:text-gray-300"/>
                        </button>
                        <div
                            className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 px-16 text-center font-bold text-gray-900 dark:text-white text-lg truncate">
                            {getPageTitle()}
                        </div>
                        <div className="relative z-10 flex items-center gap-2">
                            {!isOnline && (
                                <WifiOff
                                    size={18}
                                    className="text-red-500 dark:text-red-400"
                                    title={t("sidebar.offline")}
                                />
                            )}
                            <div
                                className="cursor-pointer"
                                onClick={() => navigate("/select-account")}
                                title={t("sidebar.changeAccount")}
                            >
                                <div
                                    className="w-9 h-9 rounded-full bg-gray-800 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600 hover:border-green-500 transition-colors overflow-hidden">
                                    <Avatar/>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Page content ── */}
                <div className="flex-1 overflow-y-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={location.pathname}
                            initial={{opacity: 0, x: 20}}
                            animate={{opacity: 1, x: 0}}
                            exit={{opacity: 0, x: -20}}
                            transition={{duration: 0.3}}
                            className="w-full h-full"
                        >
                            <Outlet/>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};

export default Sidebar;
